// The Curator API's responses as Studio reads them: the contract's own types
// (src/exchange/curator-api.ts), which Studio's client decodes every response
// with. See Design > Exchange, "Curator API". The responses in
// studio/test/fixtures match the contract: test/studio-fixtures.test.ts
// decodes them with its schemas.

import { Schema } from "effect";
import { INTERACTION_STATES, type ActivationSummary, type DemandResponse as ContractDemand, type EventsResponse as ContractEvents, type GoldenResponse as ContractGolden, type HubSecretsSummary, type HubsResponse, type HubSummary, type InteractionState, type ToolsetSummary } from "../../../src/exchange/curator-api.ts";

export type { HubSecretsSummary, HubSummary };

export type HubManifest = HubSummary["manifest"];
export type Intent = HubManifest["intents"][number];
export type Param = Intent["params"][number];
/** A toolset, hub record and stats exactly as the contract decodes them. */
export type Toolset = ToolsetSummary;
export type HubRecord = HubSummary["hub"];
export type HubStats = NonNullable<HubSummary["stats"]>;

/** GET /api/v1/hubs/:hub/toolsets: the hub record and its newest toolsets. */
export type ToolsetsResponse = { hub: HubRecord; toolsets: readonly Toolset[] };
/** GET /api/v1/hubs/:hub/activations: the hub's activations, rollbacks included, newest first. */
export type ActivationsResponse = { activations: readonly ActivationSummary[] };
/** GET /api/v1/hubs: each hub with a toolset as its HubSummary, and the records of the hubs without one. Studio routes to a hub, and names it in every request, by its Hub ID (`hub.hub.id` of its record), and shows its name only. */
export type StudioHubs = typeof HubsResponse.Type;

/** One event as the contract decodes it, with its detail read down to the fields Studio shows. */
export type EventRow = Omit<typeof ContractEvents.Type["events"][number], "detail"> & { detail: EventDetail | null };
/** The parts of an event's detail Studio reads: an answer's price ($/M) and id, a decline's reason. */
export type EventDetail = { price?: number; answerId?: string; reason?: string };
export type EventsResponse = { events: readonly EventRow[] };

export type DemandResponse = typeof ContractDemand.Type;
export type DemandList = DemandResponse["declined"];
export type DemandItem = DemandList["items"][number];

/** One golden question of `hub`, whose toolset holds it: what it expects. expectHub is null when the hub should decline it. */
export type GoldenResponse = typeof ContractGolden.Type;
export type GoldenQuestion = GoldenResponse["questions"][number];

/** The parts of a test run's POST /v1/ask response Studio reads: each hub response's outcome, hub, and intent. See Design > Exchange, "Send a request". */
export const TestRunResponse = Schema.Struct({
  questions: Schema.Array(Schema.Struct({
    ms: Schema.Finite,
    responses: Schema.Array(Schema.Struct({
      _tag: Schema.String, hub: Schema.NullOr(Schema.String),
      intent: Schema.optionalKey(Schema.String), detail: Schema.optionalKey(Schema.String), reason: Schema.optionalKey(Schema.String),
    })),
  })),
});
export type TestRunResponse = typeof TestRunResponse.Type;

/**
 * The fields Studio reads from any failure body or error: all optional,
 * because a gateway or a proxy may send anything. `interaction` is
 * InteractionClosed's: of the Interaction it returns, Studio reads only how
 * it closed.
 */
export const FailureFields = Schema.Struct({
  _tag: Schema.optionalKey(Schema.String), detail: Schema.optionalKey(Schema.String), reason: Schema.optionalKey(Schema.String),
  interaction: Schema.optionalKey(Schema.Struct({ state: Schema.Literals(INTERACTION_STATES) })),
});

/** A failed call, as Studio words it. `closedAs`: how the Interaction a declared InteractionClosed returned stands. */
export type ApiFailure = { _tag: string; status: number; detail?: string; reason?: string; requestId?: string; closedAs?: InteractionState };
